import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Send, ShieldCheck, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { toast } from "sonner";

/** Scoped conversation with cpenz about HOW to approach the recommended next move. */
export function NextMoveCoach({ projectId, initialHistory = [], unlocked = true }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const [history, setHistory] = useState(initialHistory);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { setHistory(initialHistory); }, [initialHistory]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [history.length, sending]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || sending) return;
    if (!unlocked) {
      toast(t("coach.locked_toast"));
      navigate(`/app/checkout/${projectId}`);
      return;
    }
    setSending(true);
    setInput("");
    setHistory((h) => [...h, { role: "user", text: message }]);
    try {
      const { data } = await api.post(`/projects/${projectId}/next-move/chat`, { message });
      setHistory(data.history);
    } catch (e) {
      setHistory((h) => h.slice(0, -1));
      setInput(message);
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="mt-6 rounded-3xl border border-[#7c3aed]/30 cpenz-glass-violet p-6 sm:p-7 relative overflow-hidden" data-testid="next-move-coach" aria-labelledby="coach-title">
      <div className="relative flex items-start gap-3">
        <span className="shrink-0 h-10 w-10 rounded-full bg-[#7c3aed] text-white inline-flex items-center justify-center shadow-[0_10px_24px_-8px_rgba(124,58,237,0.9)]"><Sparkles size={17} /></span>
        <div>
          <span className="text-[10px] font-semibold tracking-[0.2em] uppercase text-[#a78bfa]">{t("coach.overline")}</span>
          <h3 id="coach-title" className="text-lg font-bold text-white leading-tight">{t("coach.title")}</h3>
          <p className="mt-1 text-xs text-slate-400 leading-relaxed">{t("coach.sub")}</p>
        </div>
      </div>

      {history.length === 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="coach-suggestions">
          {t("coach.suggestions").map((s, i) => (
            <button key={i} type="button" onClick={() => send(s)} data-testid={`coach-suggestion-${i}`}
              className="text-xs font-semibold text-slate-200 border border-white/15 rounded-full px-3 py-1.5 hover:bg-white/10 transition-colors">
              {s}
            </button>
          ))}
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-4 max-h-80 overflow-y-auto space-y-3 pr-1 no-scrollbar" data-testid="coach-messages">
          {history.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                data-testid={`coach-msg-${m.role}`}
                className={`max-w-[88%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                  m.role === "user" ? "bg-white text-[#0b0f1a]"
                    : m.kind === "refused" ? "bg-red-500/10 border border-red-400/30 text-red-100"
                    : m.kind === "redirect" ? "bg-[#7c3aed]/15 border border-[#a78bfa]/30 text-slate-100"
                    : "bg-white/10 text-slate-100"
                }`}
              >
                {String(m.text).replace(/\*\*/g, "").split("\n").map((line, j) => <p key={j} className={j ? "mt-1.5" : ""}>{line}</p>)}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start"><div className="rounded-2xl px-4 py-2.5 bg-white/10 text-slate-300 text-sm inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> {t("coach.thinking")}</div></div>
          )}
          <div ref={endRef} />
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-4 flex gap-2">
        <input
          data-testid="coach-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t("coach.placeholder")}
          maxLength={600}
          disabled={sending}
          className="flex-1 rounded-full bg-white/10 border border-white/15 px-4 h-10 text-sm text-white placeholder:text-slate-500 outline-none focus:border-[#a78bfa]"
        />
        <button type="submit" data-testid="coach-send" disabled={sending || input.trim().length < 2}
          className="rounded-full bg-white text-[#0b0f1a] w-10 h-10 inline-flex items-center justify-center hover:bg-white/90 disabled:opacity-40">
          {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
        </button>
      </form>
      <p className="mt-3 inline-flex items-start gap-1.5 text-[11px] text-slate-500 leading-relaxed"><ShieldCheck size={12} className="mt-0.5 shrink-0" /> {t("coach.policy")}</p>
    </section>
  );
}
